import React, { useMemo } from "react";
import { useQuery } from "@apollo/client";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { alphaFromHex } from "../../util/colors";
import { GET_ORGANIZATION_DIVISION_SUMMARY } from "./queries/GetOrganizations";
import useTranslation from "../../i18n/useTranslation";
import { getLocalizedGraphQLError } from "../../util/graphqlError";
import useThemeColors from "./portal/useThemeColors";

function normalizeStatus(status) {
    if (!status) return "UNKNOWN";

    return String(status).toUpperCase();
}

function SummaryCard({ icon, label, value, hint, colors }) {
    return (
        <div className="col-12 col-sm-6 col-lg-3">
            <div
                style={{
                    borderRadius: 18,
                    border: "1px solid rgba(0,0,0,0.08)",
                    background: "#fff",
                    boxShadow: "0 12px 28px rgba(0,0,0,0.08)",
                    padding: 16,
                    minHeight: 116,
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    margin: 5,
                }}
            >
                <div
                    style={{
                        width: 46,
                        height: 46,
                        borderRadius: 16,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: colors.primary,
                        color: colors.textOnPrimary,
                        border: `1px solid ${alphaFromHex(colors.primary, 0.4)}`,
                        flexShrink: 0,
                    }}
                >
                    <i className={icon} />
                </div>

                <div style={{ minWidth: 0 }}>
                    <div
                        style={{
                            fontSize: 12,
                            fontWeight: 900,
                            letterSpacing: "0.04em",
                            textTransform: "uppercase",
                            color: "rgba(20,21,26,0.58)",
                        }}
                    >
                        {label}
                    </div>

                    <div
                        style={{
                            marginTop: 4,
                            fontSize: 26,
                            fontWeight: 950,
                            color: "#14151a",
                            lineHeight: 1,
                        }}
                    >
                        {value ?? 0}
                    </div>

                    {hint ? (
                        <div
                            style={{
                                marginTop: 6,
                                fontSize: 12,
                                fontWeight: 800,
                                color: "rgba(20,21,26,0.48)",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {hint}
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

function ChartCard({ title, subtitle, children, empty, emptyText }) {
    return (
        <div className="col-12 col-xl-4">
            <div
                style={{
                    borderRadius: 20,
                    border: "1px solid rgba(0,0,0,0.08)",
                    background: "#fff",
                    boxShadow: "0 14px 34px rgba(0,0,0,0.09)",
                    padding: 16,
                    minHeight: 360,
                }}
            >
                <div style={{ marginBottom: 12 }}>
                    <h3
                        style={{
                            margin: 0,
                            fontSize: 17,
                            fontWeight: 950,
                            color: "#14151a",
                        }}
                    >
                        {title}
                    </h3>

                    {subtitle ? (
                        <div
                            style={{
                                marginTop: 4,
                                fontSize: 12.5,
                                fontWeight: 800,
                                color: "rgba(20,21,26,0.52)",
                            }}
                        >
                            {subtitle}
                        </div>
                    ) : null}
                </div>

                {empty ? (
                    <div
                        style={{
                            minHeight: 260,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            color: "rgba(20,21,26,0.45)",
                            fontWeight: 800,
                            borderRadius: 16,
                            background: "rgba(0,0,0,0.02)",
                            border: "1px dashed rgba(0,0,0,0.12)",
                            padding: 20,
                        }}
                    >
                        {emptyText}
                    </div>
                ) : (
                    children
                )}
            </div>
        </div>
    );
}

export default function OrganizationDivisionSummaryCharts({
                                                              organizationSlug
                                                          }) {
    const colors = useThemeColors();
    const { t } = useTranslation();

    const { data, loading, error } = useQuery(GET_ORGANIZATION_DIVISION_SUMMARY, {
        variables: {
            organizationSlug,
        },
        skip: !organizationSlug,
        fetchPolicy: "cache-and-network",
    });

    const summary = data?.organizationDivisionSummary;

    const statusLabels = useMemo(() => {
        return {
            COMPLETED: t("organizationDivisionSummary.status.completed"),
            SCHEDULED: t("organizationDivisionSummary.status.scheduled"),
            PENDING: t("organizationDivisionSummary.status.pending"),
            OPEN: t("organizationDivisionSummary.status.open"),
            POSTPONED: t("organizationDivisionSummary.status.postponed"),
            CANCELLED: t("organizationDivisionSummary.status.cancelled"),
            CANCELED: t("organizationDivisionSummary.status.cancelled"),
            DONE: t("organizationDivisionSummary.status.done"),
            FINISHED: t("organizationDivisionSummary.status.finished"),
            UNKNOWN: t("organizationDivisionSummary.status.unknown"),
        };
    }, [t]);

    const teamsByDivision = useMemo(() => {
        return (summary?.teamsByDivision || [])
            .map((item) => ({
                name: item.divisionName,
                teams: item.count || 0,
            }))
            .sort((a, b) => b.teams - a.teams)
            .slice(0, 8);
    }, [summary]);

    const playersByDivision = useMemo(() => {
        return (summary?.playersByDivision || [])
            .map((item) => ({
                name: item.divisionName,
                players: item.count || 0,
            }))
            .sort((a, b) => b.players - a.players)
            .slice(0, 8);
    }, [summary]);

    const gamesByStatus = useMemo(() => {
        return (summary?.gamesByStatus || [])
            .map((item) => {
                const normalizedStatus = normalizeStatus(item.status);

                return {
                    name:
                        statusLabels[normalizedStatus] ||
                        String(item.status || "")
                            .replaceAll("_", " ")
                            .toLowerCase()
                            .replace(/\b\w/g, (c) => c.toUpperCase()),
                    value: item.count || 0,
                    status: normalizedStatus,
                };
            })
            .filter((item) => item.value > 0);
    }, [summary, statusLabels]);

    const gameStatusColors = [
        colors.primary,
        colors.secondary,
        colors.success || "#198754",
        colors.warning || "#ffc107",
        colors.danger || "#dc3545",
        colors.surface || "#2C2E36",
    ];

    const completionPercent = useMemo(() => {
        const total = summary?.totalGames || 0;
        const completed = summary?.completedGames || 0;

        if (!total) return 0;

        return Math.round((completed / total) * 100);
    }, [summary]);

    if (loading && !summary) {
        return (
            <div className="container-fluid mb-4">
                <div
                    style={{
                        borderRadius: 20,
                        padding: 24,
                        background: "#fff",
                        border: "1px solid rgba(0,0,0,0.08)",
                        boxShadow: "0 12px 28px rgba(0,0,0,0.08)",
                        fontWeight: 900,
                        color: "rgba(20,21,26,0.60)",
                    }}
                >
                    {t("organizationDivisionSummary.loading")}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container-fluid mb-4">
                <div className="alert alert-warning">
                    {getLocalizedGraphQLError(error, t, "organizationDivisionSummary.error")}
                </div>
            </div>
        );
    }

    if (!summary) return null;

    return (
        <div className="container-fluid mb-4">
            <div
                style={{
                    borderRadius: 24,
                    padding: 18,
                    background: `linear-gradient(135deg, ${alphaFromHex(
                        colors.primary,
                        0.14
                    )}, rgba(255,255,255,1))`,
                    border: "1px solid rgba(0,0,0,0.08)",
                    boxShadow: "0 18px 44px rgba(0,0,0,0.08)",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 12,
                        alignItems: "flex-start",
                        flexWrap: "wrap",
                        marginBottom: 16,
                    }}
                >
                    <div>
                        <h2
                            style={{
                                margin: 0,
                                fontSize: 22,
                                fontWeight: 950,
                                color: "#14151a",
                            }}
                        >
                            {t("organizationDivisionSummary.title")}
                        </h2>

                        <div
                            style={{
                                marginTop: 5,
                                fontSize: 13,
                                fontWeight: 800,
                                color: "rgba(20,21,26,0.58)",
                            }}
                        >
                            {t("organizationDivisionSummary.subtitle")}
                        </div>
                    </div>

                    <div
                        style={{
                            minWidth: 190,
                            padding: "10px 14px",
                            borderRadius: 16,
                            background: "#fff",
                            border: "1px solid rgba(0,0,0,0.08)",
                        }}
                    >
                        <div
                            style={{
                                fontSize: 12,
                                fontWeight: 900,
                                color: "rgba(20,21,26,0.55)",
                                textTransform: "uppercase",
                                letterSpacing: "0.04em",
                            }}
                        >
                            {t("organizationDivisionSummary.seasonProgress")}
                        </div>

                        <div
                            style={{
                                marginTop: 6,
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                            }}
                        >
                            <div
                                style={{
                                    flex: 1,
                                    height: 10,
                                    borderRadius: 999,
                                    background: "rgba(0,0,0,0.08)",
                                    overflow: "hidden",
                                }}
                            >
                                <div
                                    style={{
                                        width: `${completionPercent}%`,
                                        height: "100%",
                                        borderRadius: 999,
                                        background: colors.primary,
                                    }}
                                />
                            </div>

                            <strong style={{ fontSize: 13, color: "#14151a" }}>
                                {completionPercent}%
                            </strong>
                        </div>
                    </div>
                </div>

                <div className="row g-3 mb-3">
                    <SummaryCard
                        icon="fas fa-layer-group"
                        label={t("organizationDivisionSummary.cards.divisions")}
                        value={summary.totalDivisions}
                        hint={t("organizationDivisionSummary.hints.openDivisions", {
                            count: summary.openDivisions || 0,
                        })}
                        colors={colors}
                    />

                    <SummaryCard
                        icon="fas fa-calendar-check"
                        label={t("organizationDivisionSummary.cards.activeSeasons")}
                        value={summary.activeSeasons}
                        hint={t("organizationDivisionSummary.hints.currentlyRunning")}
                        colors={colors}
                    />

                    <SummaryCard
                        icon="fas fa-users"
                        label={t("organizationDivisionSummary.cards.teams")}
                        value={summary.totalTeams}
                        hint={t("organizationDivisionSummary.hints.registeredTeams")}
                        colors={colors}
                    />

                    <SummaryCard
                        icon="fas fa-user-friends"
                        label={t("organizationDivisionSummary.cards.players")}
                        value={summary.totalPlayers}
                        hint={t("organizationDivisionSummary.hints.registeredPlayers")}
                        colors={colors}
                    />

                    <SummaryCard
                        icon="fas fa-trophy"
                        label={t("organizationDivisionSummary.cards.games")}
                        value={summary.totalGames}
                        hint={t("organizationDivisionSummary.hints.completedGames", {
                            count: summary.completedGames || 0,
                        })}
                        colors={colors}
                    />

                    <SummaryCard
                        icon="fas fa-check-circle"
                        label={t("organizationDivisionSummary.cards.completed")}
                        value={summary.completedGames}
                        hint={t("organizationDivisionSummary.hints.finishedGames")}
                        colors={colors}
                    />

                    <SummaryCard
                        icon="fas fa-clock"
                        label={t("organizationDivisionSummary.cards.scheduled")}
                        value={summary.scheduledGames}
                        hint={t("organizationDivisionSummary.hints.upcomingPending")}
                        colors={colors}
                    />

                    <SummaryCard
                        icon="fas fa-exclamation-triangle"
                        label={t("organizationDivisionSummary.cards.issues")}
                        value={
                            (summary.cancelledGames || 0)
                        }
                        hint={t("organizationDivisionSummary.hints.postponedCancelled")}
                        colors={colors}
                    />
                </div>

                <div className="row g-3">
                    <ChartCard
                        title={t("organizationDivisionSummary.charts.teamsByDivision.title")}
                        subtitle={t(
                            "organizationDivisionSummary.charts.teamsByDivision.subtitle"
                        )}
                        empty={!teamsByDivision.length}
                        emptyText={t("organizationDivisionSummary.noData")}
                    >
                        <div style={{ width: "100%", height: 270 }}>
                            <ResponsiveContainer>
                                <BarChart
                                    data={teamsByDivision}
                                    layout="vertical"
                                    margin={{ top: 8, right: 20, left: 30, bottom: 8 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                    <XAxis type="number" allowDecimals={false} />
                                    <YAxis
                                        type="category"
                                        dataKey="name"
                                        width={95}
                                        tick={{ fontSize: 12, fontWeight: 700 }}
                                    />
                                    <Tooltip />
                                    <Bar
                                        dataKey="teams"
                                        name={t("organizationDivisionSummary.chartLabels.teams")}
                                        radius={[0, 10, 10, 0]}
                                        fill={colors.primary}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </ChartCard>

                    <ChartCard
                        title={t("organizationDivisionSummary.charts.playersByDivision.title")}
                        subtitle={t(
                            "organizationDivisionSummary.charts.playersByDivision.subtitle"
                        )}
                        empty={!playersByDivision.length}
                        emptyText={t("organizationDivisionSummary.noData")}
                    >
                        <div style={{ width: "100%", height: 270 }}>
                            <ResponsiveContainer>
                                <BarChart
                                    data={playersByDivision}
                                    layout="vertical"
                                    margin={{ top: 8, right: 20, left: 30, bottom: 8 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                    <XAxis type="number" allowDecimals={false} />
                                    <YAxis
                                        type="category"
                                        dataKey="name"
                                        width={95}
                                        tick={{ fontSize: 12, fontWeight: 700 }}
                                    />
                                    <Tooltip />
                                    <Bar
                                        dataKey="players"
                                        name={t("organizationDivisionSummary.chartLabels.players")}
                                        radius={[0, 10, 10, 0]}
                                        fill={colors.secondary || colors.primary}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </ChartCard>

                    <ChartCard
                        title={t("organizationDivisionSummary.charts.gamesByStatus.title")}
                        subtitle={t(
                            "organizationDivisionSummary.charts.gamesByStatus.subtitle"
                        )}
                        empty={!gamesByStatus.length}
                        emptyText={t("organizationDivisionSummary.noData")}
                    >
                        <div style={{ width: "100%", height: 270 }}>
                            <ResponsiveContainer>
                                <PieChart>
                                    <Pie
                                        data={gamesByStatus}
                                        dataKey="value"
                                        nameKey="name"
                                        innerRadius={65}
                                        outerRadius={95}
                                        paddingAngle={3}
                                    >
                                        {gamesByStatus.map((entry, index) => (
                                            <Cell
                                                key={`${entry.status}-${index}`}
                                                fill={
                                                    gameStatusColors[
                                                    index % gameStatusColors.length
                                                        ]
                                                }
                                            />
                                        ))}
                                    </Pie>

                                    <Tooltip />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </ChartCard>
                </div>
            </div>
        </div>
    );
}
